import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";

/**
 * Phase 7C-4: what the auth gate in `App.tsx` is showing.
 *
 * Starts as `checking` on purpose: the very first render must be the
 * splash, never protected UI (a cached actor is not proof of anything)
 * and never a login flash on a refresh that is about to succeed.
 *
 *   checking     startup / revalidation in flight
 *   signedOut    no usable session -> login page
 *   signedIn     the Backend confirmed the token via SESSION_ME
 *   unreachable  a token exists but the Backend could not be reached to
 *                confirm it; the token is KEPT and a retry is offered
 */
export type AuthStatus = 'checking' | 'signedOut' | 'signedIn' | 'unreachable';

/**
 * Why the app is showing the login page.
 *
 *   none            the ordinary case: never signed in, or the user pressed
 *                   Logout themselves. Say nothing.
 *   sessionEnded    the user did NOT ask to be signed out and the Backend
 *                   would not say why. The login page names both
 *                   possibilities, because otherwise being thrown out
 *                   mid-work looks like a bug.
 *   revokedByAdmin  the Backend said, in so many words, that an
 *                   administrator signed this device out.
 *
 * The Backend still answers 401 identically for a replaced and an expired
 * session, so `sessionEnded` must keep naming both. The ONE exception it
 * now discloses is an administrator revoke, and only to a caller that
 * presented a token which really existed -- see
 * `common/session.py::revocation_reason` for why that trade is worth
 * making. Without it every employee an administrator signs out is told
 * their account was accessed from another device, which is false and
 * invites them to treat it as a break-in.
 */
export type SignOutReason = 'none' | 'sessionEnded' | 'revokedByAdmin';

export type AuthActor = {
    id: number;
    name: string;
    code: string;
    privilege: number;
    privilege_name: string;
    default_page: string;
    /**
     * UI-preview only: the "switch role to test permissions" tools in
     * nav.tsx / employee-detail.tsx set these to preview menu visibility.
     * Never sent by the Backend, never persisted, and not a security
     * boundary -- Backend business-route enforcement is Phase 7C-5.
     */
    role?: string;
    permissions?: string[];
}

type userState = AuthActor & {
    authStatus: AuthStatus;
    signOutReason: SignOutReason;
}

const actorInitialValues: AuthActor = {
    id: 0,
    name: "",
    code: "",
    privilege: 0,
    privilege_name: "",
    default_page: ""
}

const initialValues: userState = {
    ...actorInitialValues,
    authStatus: 'checking',
    signOutReason: 'none'
}

const assignActor = (state: userState, actor: AuthActor) => {
    state.id = Number(actor.id) || 0;
    state.name = actor.name ?? "";
    state.code = actor.code ?? "";
    state.privilege = Number(actor.privilege) || 0;
    state.privilege_name = actor.privilege_name ?? "";
    state.default_page = actor.default_page ?? "";
    state.role = actor.role;
    state.permissions = Array.isArray(actor.permissions) ? [...actor.permissions] : undefined;
}

const userSlice = createSlice({
    name: "user",
    initialState: initialValues,
    reducers: {
        // Resets the actor fields only; `authStatus` is owned by the
        // session actions below. (The previous body, `state = initialValues`,
        // reassigned the draft parameter and therefore never changed the
        // store -- after a logout the old employee stayed in Redux.)
        initailData: (state: userState) => {
            assignActor(state, actorInitialValues);
        },
        increase: (state: userState, action: PayloadAction<AuthActor>) => {
            assignActor(state, action.payload);
        },
        /** Phase 7C-4: the Backend confirmed who this is. */
        sessionEstablished: (state: userState, action: PayloadAction<AuthActor>) => {
            assignActor(state, action.payload);
            state.authStatus = 'signedIn';
            // A successful sign-in answers whatever the last notice said.
            state.signOutReason = 'none';
        },
        /**
         * Phase 7C-4: no session -- clear the actor, show the login page.
         * The payload says whether the user asked for this; it defaults to
         * 'none' so a caller that does not care cannot accidentally accuse
         * another device.
         */
        sessionCleared: (state: userState, action: PayloadAction<SignOutReason | undefined>) => {
            assignActor(state, actorInitialValues);
            state.authStatus = 'signedOut';
            state.signOutReason = action.payload ?? 'none';
        },
        /** Phase 7C-4: `checking` / `unreachable` transitions. */
        setAuthStatus: (state: userState, action: PayloadAction<AuthStatus>) => {
            state.authStatus = action.payload;
        },
        /** Phase 7C-4: the user has read the "you were signed out" notice. */
        dismissSignOutNotice: (state: userState) => {
            state.signOutReason = 'none';
        }
    },
    extraReducers: (builder) => {

    }
});

export const {
    increase,
    initailData,
    sessionEstablished,
    sessionCleared,
    setAuthStatus,
    dismissSignOutNotice,
} = userSlice.actions;
export const userSelector = (store: RootState) => store.userReducer;
export const authStatusSelector = (store: RootState): AuthStatus => store.userReducer.authStatus;
export const signOutReasonSelector = (store: RootState): SignOutReason => store.userReducer.signOutReason;
export default userSlice.reducer;
